import { stringIds } from "@salestrak/forms";

import { lifecycleVerb, type ActivityNode } from "./activity-types";

export { formatDate, formatTime } from "#app/lib/utils/format-date";
export { formatRelativeDate } from "#app/lib/utils/format-relative-date";
// Day-group label ("Today"/"Yesterday"/"Monday, June 3rd") lives in the
// generic date-jump module, shared with every day-grouped feed.
export { getDateLabel } from "@salestrak/ui";

// `pluralize` lives in `#app/lib/utils/pluralize` (a pure, non-React home);
// re-exported here for the activity-feed importers.
export { pluralize } from "#app/lib/utils/pluralize";

export function photoFieldDelta(change: { before?: unknown; after?: unknown } | undefined) {
  const before = stringIds(change?.before);
  const after = stringIds(change?.after);
  return { added: after.filter((id) => !before.includes(id)), removed: before.filter((id) => !after.includes(id)) };
}

export function isPhotoField(activity: ActivityNode, fieldId: string): boolean {
  return (activity.fields ?? []).some((f) => f.customFieldId === fieldId && f.type === "photos");
}

/**
 * Generate a human-readable one-liner from an activity changeset.
 * Used by the feed screen's ActivityItem and other summary contexts.
 */
export function describeActivity(cs: Record<string, any> | null): string {
  if (!cs) return "Activity recorded";

  if (Array.isArray(cs)) {
    const first = cs[0];
    if (!first) return "Activity recorded";
    const field = first.field ?? first.subject ?? "";
    if (field === "age") {
      const status = first.to?.status ?? first.newValue?.status;
      return status ? `Follow up updated to ${status}` : "Follow up updated";
    }
    return `${field} updated`;
  }

  const subjects = Object.keys(cs);
  const parts: string[] = [];

  for (const subject of subjects) {
    switch (lifecycleVerb(subject) ?? subject) {
      case "age": {
        const status = cs[subject]?.after?.status ?? cs[subject]?.to?.status;
        parts.push(status ? `Follow up updated to ${status}` : "Follow up updated");
        break;
      }
      case "stageId": parts.push("Moved stage"); break;
      case "note": parts.push("Added a comment"); break;
      case "noteReply": parts.push("Replied to a comment"); break;
      case "assignees": parts.push("Updated assignees"); break;
      case "teams": parts.push("Updated teams"); break;
      case "task": {
        if (cs[subject]?.after !== undefined && cs[subject]?.before === undefined)
          parts.push("Created Project");
        else parts.push("Updated Project");
        break;
      }
      case "archived": parts.push("Archived Project"); break;
      case "unarchived": parts.push("Unarchived Project"); break;
      case "trashed": parts.push("Trashed Project"); break;
      case "restored": parts.push("Restored Project"); break;
      case "values": parts.push("Updated fields"); break;
      case "form": parts.push("Submitted a form"); break;
      case "attachments": parts.push("Uploaded files"); break;
      case "formSubmission": parts.push("Submitted a form"); break;
      case "address": parts.push("Updated address"); break;
      case "issueTypeId": parts.push("Changed Project type"); break;
      case "name": parts.push("Renamed Project"); break;
      case "startDate": parts.push("Updated start date"); break;
      case "targetDate": parts.push("Updated target date"); break;
      default: parts.push(`${subject} updated`); break;
    }
  }

  return parts.length > 0 ? parts.join(", ") : "Activity recorded";
}
